import { format as dateFnsFormat, parseISO } from 'date-fns';

/**
 * Safely formats a date from ISO string or timestamp, returning a fallback string if invalid
 */
export const safeFormatDate = (
  dateInput: string | number | undefined | null,
  formatString: string,
  fallback: string = '-'
): string => {
  if (dateInput === undefined || dateInput === null) {
    return fallback;
  }

  try {
    let date: Date;

    if (typeof dateInput === 'string') {
      // Parse ISO-8601 string
      date = parseISO(dateInput);
    } else {
      // Handle timestamp (number)
      // Validate timestamp is within reasonable range
      const MIN_VALID_TIMESTAMP = -2208988800000; // ~1900
      const MAX_VALID_TIMESTAMP = 4102444800000; // ~2100

      if (dateInput < MIN_VALID_TIMESTAMP || dateInput > MAX_VALID_TIMESTAMP) {
        console.warn(`Invalid timestamp value: ${dateInput} (out of range)`);
        return fallback;
      }

      date = new Date(dateInput);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date value: ${dateInput} (NaN)`);
      return fallback;
    }

    return dateFnsFormat(date, formatString);
  } catch (error) {
    console.error(`Error formatting date ${dateInput}:`, error);
    return fallback;
  }
};

/**
 * Validates if a date input (ISO string or timestamp) is valid
 */
export const isValidDate = (dateInput: string | number | undefined | null): boolean => {
  if (dateInput === undefined || dateInput === null) {
    return false;
  }

  try {
    let date: Date;

    if (typeof dateInput === 'string') {
      date = parseISO(dateInput);
    } else {
      const MIN_VALID_TIMESTAMP = -2208988800000; // ~1900
      const MAX_VALID_TIMESTAMP = 4102444800000; // ~2100

      if (dateInput < MIN_VALID_TIMESTAMP || dateInput > MAX_VALID_TIMESTAMP) {
        return false;
      }

      date = new Date(dateInput);
    }

    return !isNaN(date.getTime());
  } catch (error) {
    return false;
  }
};

/**
 * Safely creates a Date object from ISO string or timestamp, returning null if invalid
 */
export const safeCreateDate = (dateInput: string | number | undefined | null): Date | null => {
  if (!isValidDate(dateInput)) {
    return null;
  }

  try {
    if (typeof dateInput === 'string') {
      return parseISO(dateInput);
    } else {
      return new Date(dateInput!);
    }
  } catch (error) {
    console.error(`Error creating date from input ${dateInput}:`, error);
    return null;
  }
};
